import { msg } from '@lingui/core/macro';
import type { MessageDescriptor } from '@lingui/core';
import type { FontAwesome6 } from '@expo/vector-icons';
import type { ComponentProps } from 'react';

export const DOCS_BASE = 'https://docs.paperzilla.ai';
export const CLAUDE_GUIDE = `${DOCS_BASE}/guides/claude`;
export const MCP_GUIDE = `${DOCS_BASE}/guides/mcp`;
export const CREATE_FEED = '/?openChat=true&entrySurface=research-briefs';

export type BriefPick = { paper: MessageDescriptor; href: string; note: MessageDescriptor };

export type BriefExample = {
    key: string;
    icon: ComponentProps<typeof FontAwesome6>['name'];
    tab: MessageDescriptor;
    feed: MessageDescriptor;
    picks: BriefPick[];
    question: MessageDescriptor;
    answer: MessageDescriptor;
    source: { label: MessageDescriptor; href: string };
};

// Real, published papers. Every claim in a note or answer must be checkable
// against the linked abstract or full text.
export const briefExamples: BriefExample[] = [
    {
        key: 'llm-judges',
        icon: 'robot',
        tab: msg`LLM evaluation`,
        feed: msg`Reliable LLM-as-a-judge evaluation`,
        picks: [
            {
                paper: msg({ message: 'Prompt Lottery in LLM-as-a-Judge', comment: 'Original paper title; keep it recognisable for source lookup.' }),
                href: 'https://arxiv.org/abs/2610.38076',
                note: msg`Start here if you rank models with one judge prompt. Rewording the prompt without changing its meaning shifted verdicts, and sometimes reversed model rankings.`,
            },
            {
                paper: msg({ message: 'The Judge Can Debias Itself', comment: 'Original paper title; keep it recognisable for source lookup.' }),
                href: 'https://arxiv.org/abs/2610.24320',
                note: msg`A fix you can try today. Asking the judge to review its own verdict for bias helped in 20 of 21 model and bias combinations, without extra training.`,
            },
        ],
        question: msg`How much worse are LLM judges than people at this?`,
        answer: msg`Clearly worse. In a user study with 10 annotators, the judges’ agreement dropped 6 to 9 times more than the humans’ when prompts were reworded. The authors recommend combining verdicts from several prompt variants and trusting the ones that stay consistent.`,
        source: { label: msg`Read the abstract`, href: 'https://arxiv.org/abs/2610.38076' },
    },
    {
        key: 'glp1-alcohol',
        icon: 'dna',
        tab: msg`Biomedicine`,
        feed: msg`GLP-1 drugs and alcohol use`,
        picks: [
            {
                paper: msg({ message: 'Oral Semaglutide for Alcohol Use Disorder: A Randomized Clinical Trial', comment: 'Original paper title; keep it recognisable for source lookup.' }),
                href: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC13567346/',
                note: msg`Start here. In 50 adults over 8 weeks, oral semaglutide reduced heavy drinking days compared with placebo. It missed its primary outcome: craving measured in the lab.`,
            },
            {
                paper: msg({ message: 'Alcohol Use Following GLP-1 Receptor Agonist Therapy in the Cooper Center Longitudinal Study', comment: 'Original paper title; keep it recognisable for source lookup.' }),
                href: 'https://pubmed.ncbi.nlm.nih.gov/42486225/',
                note: msg`Real-world context. Among 11,258 clinic patients who drink, those who started a GLP-1 drug cut about one more drink a week than the others.`,
            },
        ],
        question: msg`Is that just because people lost weight?`,
        answer: msg`Possibly. In the clinic cohort, the difference was no longer significant after adjusting for weight change (P = .10), and the authors link it to weight loss. Only 256 of the 11,258 patients started a GLP-1 drug. The randomized trial is the better test of a direct effect, but with 50 people it is small.`,
        source: { label: msg`Read the cohort study`, href: 'https://pubmed.ncbi.nlm.nih.gov/42486225/' },
    },
    {
        key: 'urban-heat',
        icon: 'city',
        tab: msg`Urban climate`,
        feed: msg`Urban heat and walkability`,
        picks: [
            {
                paper: msg({ message: 'CoolPath Tool: A Thermal Comfort Path Planning Tool for Urban Mobility', comment: 'Original paper title; keep it recognisable for source lookup.' }),
                href: 'https://arxiv.org/abs/2602.02540',
                note: msg`Start here. For a one-mile walk in Austin, Google Maps suggested the route with the highest heat stress of three options. The tool found a cooler way to the same place.`,
            },
            {
                paper: msg({ message: 'CoolWalks for active mobility in urban street networks', comment: 'Original paper title; keep it recognisable for source lookup.' }),
                href: 'https://doi.org/10.1038/s41598-025-97200-2',
                note: msg`Read this if you compare neighborhoods. A regular grid with uniform building heights offers walkers no shadier option than the shortest path. Varied streets and heights do.`,
            },
        ],
        question: msg`Did the cooler route in Austin mean a longer walk?`,
        answer: msg`Barely. All three routes were about a mile, and the authors call the difference in length negligible. The model classified 46% of the coolest route as shaded, against about 34% of the hottest.`,
        source: { label: msg`See Figure 2 in the paper`, href: 'https://arxiv.org/html/2602.02540v2#S3.F2' },
    },
];

export const whyItWorks = [
    {
        number: '01',
        title: msg`It picks, it doesn’t list`,
        body: msg`Paperzilla narrows thousands of new papers a day to the ones that match your topic. Your assistant narrows those to the few worth reading today.`,
    },
    {
        number: '02',
        title: msg`It explains in your terms`,
        body: msg`Your assistant knows what you are working on, so “why it matters” is about your project, not a rewritten abstract.`,
    },
    {
        number: '03',
        title: msg`It answers follow-ups`,
        body: msg`Ask about methods, data, or limitations. Answers come from the paper itself, with a pointer to the section or figure.`,
    },
    {
        number: '04',
        title: msg`It gets better over time`,
        body: msg`Your feedback on the papers it brings you goes back to your feed, so the ranking keeps improving.`,
    },
];

export const weekOfBriefs = [
    { day: msg`Mon`, title: msg`Two worth reading`, body: msg`A new method, and a dataset you could reuse.` },
    { day: msg`Tue`, title: msg`A quiet day`, body: msg`Nothing new worth your time. That is the whole brief.` },
    { day: msg`Wed`, title: msg`One that pushes back`, body: msg`A study that questions an assumption in your own work. Better to know now.` },
    { day: msg`Thu`, title: msg`One from next door`, body: msg`A paper from a neighboring field, with a method you could borrow.` },
    { day: msg`Fri`, title: msg`The weekly roundup`, body: msg`One summary across all your feeds, with the week’s must-read on top.` },
];

export const followUpPrompts = [
    { label: msg`Triage`, prompt: msg`I have ten minutes. Which one should I read?` },
    { label: msg`Methods`, prompt: msg`What data did they use, and how much of it?` },
    { label: msg`Skeptic`, prompt: msg`What would a tough reviewer say about this?` },
    { label: msg`Apply`, prompt: msg`Could this work on our data? What would we need?` },
    { label: msg`Compare`, prompt: msg`How does this compare with the other papers in my feed?` },
    { label: msg`Explain`, prompt: msg`Explain the main result as you would to a new PhD student.` },
];

export const connectionFlow = [
    { label: msg`Sources`, title: msg`New papers`, body: msg`arXiv, bioRxiv, medRxiv, PubMed, and more` },
    { label: msg`Paperzilla`, title: msg`Your feeds`, body: msg`Ranked against each of your topics, every day` },
    { label: msg`MCP`, title: msg`The connection`, body: msg`Your assistant signs in and reads your feeds` },
    { label: msg`Assistant`, title: msg`Claude, ChatGPT, and others`, body: msg`Picks, explains, and answers your questions` },
    { label: msg`You`, title: msg`08:00`, body: msg`A brief that is ready with your coffee` },
];

export const setupSteps = [
    {
        number: '01',
        title: msg`Create a research feed`,
        body: msg`Describe your topic in a few sentences. Paperzilla builds the feed and starts collecting matching papers. Already have one? Skip ahead.`,
    },
    {
        number: '02',
        title: msg`Connect your assistant`,
        body: msg`Add the Paperzilla plugin in Claude or ChatGPT, or connect any AI assistant that supports MCP. Then sign in with your Paperzilla account.`,
    },
    {
        number: '03',
        title: msg`Schedule your morning brief`,
        body: msg`Ask for a first brief to see what you get. Then save it as a daily task, and it is waiting for you every morning.`,
    },
];

export const assistants = [
    {
        name: msg({ message: 'Claude', comment: 'Product name' }),
        status: msg`Plugin and connector`,
        body: msg`Includes the Morning Brief skill. Schedule it as a recurring task.`,
        link: { label: msg`Claude guide ↗`, href: CLAUDE_GUIDE },
    },
    {
        name: msg({ message: 'ChatGPT', comment: 'Product name' }),
        status: msg`Plugin`,
        body: msg`Add the Paperzilla plugin and sign in with your Paperzilla account.`,
        link: { label: msg`ChatGPT guide ↗`, href: `${MCP_GUIDE}#chatgpt` },
    },
    {
        name: msg`Any MCP assistant`,
        status: msg`MCP connection`,
        body: msg`OpenClaw, or any other assistant that supports MCP. Sign in with your Paperzilla account and schedule briefs in your own setup.`,
        link: { label: msg`MCP guide ↗`, href: MCP_GUIDE },
    },
    {
        name: msg({ message: 'Google Gemini', comment: 'Product name' }),
        status: msg`Planned`,
        body: msg`Support is on the way.`,
        link: null,
    },
];

export const faqItems = [
    {
        question: msg`Why not just ask my assistant what is new, without Paperzilla?`,
        answer: msg`You can, but web search is a poor way to keep up with a field. It favors popular and older pages, can easily miss yesterday’s preprints, and turns up different results each time. Paperzilla checks arXiv, bioRxiv, medRxiv, PubMed, and more every day, ranks each new paper against your topic, and keeps track of when each one arrived. Your assistant starts from that complete, ranked list instead of whatever a search happens to find.`,
        link: null,
    },
    {
        question: msg`What do I need?`,
        answer: msg`A Paperzilla account with at least one research feed, and an AI assistant that supports MCP connectors. Scheduled tasks may need a paid plan with your assistant.`,
        link: { label: msg`See Paperzilla plans →`, href: '/pricing' },
    },
    {
        question: msg`What can my assistant see and do?`,
        answer: msg`It reads your feeds and the papers in them. Anything that changes your account asks for your approval first, and you can disconnect it at any time.`,
        link: { label: msg`Manage AI connections →`, href: '/ai-connections' },
    },
    {
        question: msg`Does it read the whole paper?`,
        answer: msg`When you ask a detailed question, your assistant can open the full text where it is available, and point you to the section or figure it used.`,
        link: null,
    },
    {
        question: msg`What happens on a day without new papers?`,
        answer: msg`The brief says so. A quiet day is useful information, and it gives you your morning back.`,
        link: null,
    },
    {
        question: msg`Can I follow several topics?`,
        answer: msg`Yes. Create a feed for each topic, then get one brief across all of them or one per feed.`,
        link: null,
    },
];
